import type { BufferGeometry } from 'three'
import type { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import type { GLTFLoader, GLTFParser } from 'three/addons/loaders/GLTFLoader.js'
import type {
  DracoAttributeExpectation,
  DracoDecodeRequest,
  IDracoDecodeService,
} from '@/shared/draco/DracoDecodeService'
import { DracoBudgetExceededError } from '@/shared/draco/DracoDecodeService'

const EXTENSION = 'KHR_draco_mesh_compression'
const decodedGeometries = new WeakSet<BufferGeometry>()

export function isDracoGeometry(geometry: BufferGeometry): boolean {
  return decodedGeometries.has(geometry)
}
const ATTRIBUTE_NAMES: Readonly<Record<string, string>> = {
  POSITION: 'position',
  NORMAL: 'normal',
  TANGENT: 'tangent',
  TEXCOORD_0: 'uv',
  TEXCOORD_1: 'uv1',
  TEXCOORD_2: 'uv2',
  TEXCOORD_3: 'uv3',
  COLOR_0: 'color',
  WEIGHTS_0: 'skinWeight',
  JOINTS_0: 'skinIndex',
}
const COMPONENT_COUNTS: Readonly<Record<string, number>> = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
}
const COMPONENT_BYTES: Readonly<Record<number, number>> = {
  5120: 1,
  5121: 1,
  5122: 2,
  5123: 2,
  5125: 4,
  5126: 4,
}

interface Descriptor {
  readonly view: number
  readonly inputBytes: number
  readonly knownOutputBytes: number
  readonly ids: Readonly<Record<string, number>>
  readonly attributes: Readonly<Record<string, DracoAttributeExpectation>>
  readonly indexCount?: number
}

interface ParserWithViews {
  json: Record<string, any>
  loadBufferView(index: number): Promise<ArrayBuffer>
}

export interface DracoGltfBridgeOptions {
  readonly signal?: AbortSignal
  readonly source?: string
  readonly maxKnownBytes?: number
  readonly maxGeometryBytes?: number
  /** 用于在检查取消前登记解码结果的包级资源跟踪器。 */
  readonly onGeometry?: (geometry: BufferGeometry) => void
  readonly onDecode?: (
    pending: Promise<import('@/shared/draco/DracoDecodeService').DracoDecodeResult>
  ) => void
}

function nameOf(semantic: string): string {
  return ATTRIBUTE_NAMES[semantic] ?? semantic.toLowerCase()
}

function safeIndex(value: unknown, length: number): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0 && (value as number) < length
}

function collectDescriptors(json: Record<string, any>, source: string): Descriptor[] {
  const descriptors: Descriptor[] = []
  const views = json.bufferViews ?? []
  const buffers = json.buffers ?? []
  const accessors = json.accessors ?? []
  for (const mesh of json.meshes ?? []) {
    for (const primitive of mesh.primitives ?? []) {
      const extension = primitive.extensions?.[EXTENSION]
      if (!extension) continue
      if (!safeIndex(extension.bufferView, views.length))
        throw new Error(`${EXTENSION} bufferView 无效: ${source}`)
      const view = views[extension.bufferView]
      const offset = view.byteOffset ?? 0
      const buffer = buffers[view.buffer]
      if (
        !buffer ||
        !Number.isSafeInteger(offset) ||
        offset < 0 ||
        !Number.isSafeInteger(view.byteLength) ||
        view.byteLength < 1 ||
        !Number.isSafeInteger(offset + view.byteLength) ||
        offset + view.byteLength > buffer.byteLength
      )
        throw new Error(`${EXTENSION} 压缩范围越界: ${source}`)
      if (!extension.attributes || typeof extension.attributes !== 'object')
        throw new Error(`${EXTENSION} 属性映射无效: ${source}`)
      const ids: Record<string, number> = {}
      const attributes: Record<string, DracoAttributeExpectation> = {}
      let knownOutputBytes = 0
      for (const [semantic, id] of Object.entries(extension.attributes)) {
        const accessorIndex = primitive.attributes?.[semantic]
        if (
          !safeIndex(accessorIndex, accessors.length) ||
          !Number.isSafeInteger(id) ||
          (id as number) < 0
        )
          throw new Error(`${EXTENSION} 属性 ID/accessor 无效: ${source}`)
        const accessor = accessors[accessorIndex]
        const itemSize = COMPONENT_COUNTS[accessor.type]
        const componentBytes = COMPONENT_BYTES[accessor.componentType]
        if (
          !itemSize ||
          !componentBytes ||
          !Number.isSafeInteger(accessor.count) ||
          accessor.count < 1
        )
          throw new Error(`${EXTENSION} accessor 描述无效: ${source}`)
        knownOutputBytes += accessor.count * Math.ceil((itemSize * componentBytes) / 4) * 4
        const name = nameOf(semantic)
        ids[name] = id as number
        attributes[name] = {
          count: accessor.count,
          itemSize,
          componentType: accessor.componentType,
          normalized: accessor.normalized === true,
        }
      }
      if (!attributes.position) throw new Error(`${EXTENSION} 缺少 POSITION: ${source}`)
      const mode = primitive.mode ?? 4
      if (mode !== 4 && mode !== 5)
        throw new Error(`${EXTENSION} 不支持 primitive mode ${mode}: ${source}`)
      const indexCount =
        primitive.indices === undefined
          ? undefined
          : safeIndex(primitive.indices, accessors.length)
            ? accessors[primitive.indices].count
            : NaN
      if (indexCount !== undefined && (!Number.isSafeInteger(indexCount) || indexCount < 3))
        throw new Error(`${EXTENSION} indices accessor 无效: ${source}`)
      knownOutputBytes += (indexCount ?? 0) * 4
      if (!Number.isSafeInteger(knownOutputBytes))
        throw new Error(`${EXTENSION} 已知输出成本溢出: ${source}`)
      // Draco Worker 已产出三角列表；r180/r186 GLTFLoader 都会对 mode=5 再转换。
      if (mode === 5) primitive.mode = 4
      descriptors.push({
        view: extension.bufferView,
        inputBytes: view.byteLength,
        knownOutputBytes,
        ids,
        attributes,
        indexCount,
      })
    }
  }
  return descriptors
}

function sameIds(left: Readonly<Record<string, number>>, right: Readonly<Record<string, number>>) {
  const keys = Object.keys(left)
  return keys.length === Object.keys(right).length && keys.every((key) => left[key] === right[key])
}

/** Three r180/r186 的私有桥接签名集中在此模块；业务层仅提供请求上下文。 */
export function installDracoGltfBridge(
  loader: GLTFLoader,
  service: IDracoDecodeService,
  options: DracoGltfBridgeOptions = {}
): { usedDraco(): boolean } {
  const byBuffer = new WeakMap<ArrayBuffer, readonly Descriptor[]>()
  const source = options.source ?? 'unknown'
  let used = false
  const bridge = {
    preload: () => bridge,
    decodeDracoFile: (
      buffer: ArrayBuffer,
      onLoad: (geometry: BufferGeometry) => void,
      attributeIDs: Record<string, number>,
      attributeTypes: Record<string, string>,
      _colorSpace: unknown,
      onError: (error: unknown) => void
    ) => {
      const descriptor = byBuffer
        .get(buffer)
        ?.find((candidate) => sameIds(candidate.ids, attributeIDs))
      if (!descriptor) {
        onError(new Error(`${EXTENSION} 无法关联压缩视图与属性映射: ${source}`))
        return
      }
      const request: DracoDecodeRequest = {
        data: buffer,
        attributeIDs,
        attributeTypes,
        attributes: descriptor.attributes,
        indexCount: descriptor.indexCount,
        source,
        signal: options.signal,
        maxKnownBytes: options.maxKnownBytes,
        maxGeometryBytes: options.maxGeometryBytes,
      }
      const pending = service.decode(request)
      options.onDecode?.(pending)
      void pending
        .then(({ geometry }) => {
          decodedGeometries.add(geometry)
          try {
            options.onGeometry?.(geometry)
          } catch (error) {
            geometry.dispose()
            throw error
          }
          if (options.signal?.aborted) {
            geometry.dispose()
            onError(new Error(`${EXTENSION} 解码已取消: ${source}`))
            return
          }
          onLoad(geometry)
        })
        .catch((error: unknown) => onError(error))
    },
  }
  loader.setDRACOLoader(bridge as unknown as DRACOLoader)
  loader.register((parser: GLTFParser) => {
    const internal = parser as unknown as ParserWithViews
    const descriptors = collectDescriptors(internal.json, source)
    used = descriptors.length > 0
    if (used) {
      const distinct = new Set<string>()
      let geometryBytes = 0
      let transientBytes = 0
      for (const descriptor of descriptors) {
        const key = `${descriptor.view}:${JSON.stringify(descriptor.ids)}:${descriptor.indexCount}`
        if (distinct.has(key)) continue
        distinct.add(key)
        geometryBytes += descriptor.knownOutputBytes
        transientBytes += descriptor.inputBytes * 2 + descriptor.knownOutputBytes
      }
      if (options.maxGeometryBytes !== undefined && geometryBytes > options.maxGeometryBytes)
        throw new DracoBudgetExceededError('geometry', geometryBytes, options.maxGeometryBytes)
      if (options.maxKnownBytes !== undefined && transientBytes > options.maxKnownBytes)
        throw new DracoBudgetExceededError('transient', transientBytes, options.maxKnownBytes)
    }
    const byIndex = new Map<number, Descriptor[]>()
    for (const descriptor of descriptors) {
      const existing = byIndex.get(descriptor.view) ?? []
      existing.push(descriptor)
      byIndex.set(descriptor.view, existing)
    }
    const loadBufferView = internal.loadBufferView.bind(parser)
    internal.loadBufferView = async (index) => {
      const data = await loadBufferView(index)
      const matches = byIndex.get(index)
      if (matches) byBuffer.set(data, matches)
      return data
    }
    return { name: 'SHARE3D_draco_bridge' }
  })
  return { usedDraco: () => used }
}
